import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import FeatureIcon, { FeatureIconName } from '../../components/FeatureIcon';
import { SystemStatus, formatDateTime } from './statusData';

interface ServiceStatusSectionProps {
  services: SystemStatus[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

// Recognizable per-service stroke identity by existing service names.
const SERVICE_ICON: Record<string, FeatureIconName> = {
  database: 'layers',
  api: 'link',
  auth: 'shield',
  storage: 'image',
  app: 'gauge',
  chat: 'chat',
  sync: 'clock',
  maps: 'map',
  rides: 'route',
};

// Small state indicator — color is reserved for the dot so the written
// state itself stays high-contrast neutral text.
const dotColor = (status: string, dsColors: any, fallback: string) => {
  switch (status) {
    case 'operational': return dsColors.successStrong;
    case 'degraded': return dsColors.warningStrong;
    case 'outage': return dsColors.error;
    case 'maintenance': return dsColors.signalBlue;
    default: return fallback;
  }
};

const ServiceStatusSection: React.FC<ServiceStatusSectionProps> = ({
  services,
  loading,
  error,
  onRetry,
}) => {
  const { colors, dsColors } = useTheme();
  const hasRows = services.length > 0;

  return (
    <section aria-labelledby="status-service-heading" className="status-panel">
      <h2
        id="status-service-heading"
        className="status-panel-heading"
        style={{ color: colors.textPrimary }}
      >
        <FeatureIcon name="gauge" size={20} className="status-panel-icon" />
        Service status
      </h2>

      {loading && !hasRows ? (
        <p className="py-6" style={{ color: colors.textSecondary }}>
          Loading status...
        </p>
      ) : error && !hasRows ? (
        <div className="py-6">
          <p className="mb-3" style={{ color: colors.textSecondary }}>
            Unable to load service status. Please try again.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="status-retry-btn"
            style={{ color: colors.textPrimary, borderColor: colors.border }}
          >
            Retry
          </button>
        </div>
      ) : !hasRows ? (
        <p className="py-6" style={{ color: colors.textSecondary }}>
          No status data available
        </p>
      ) : (
        <>
          {error && (
            <p className="status-stale-note" style={{ color: colors.textSecondary }} role="status">
              Unable to refresh service status. Last received update is shown.
            </p>
          )}
          <ul className="status-service-grid" aria-label="Service status">
            {services.map((service) => {
              const dot = dotColor(service.status, dsColors, colors.textSecondary);
              const iconName = SERVICE_ICON[service.service_name] ?? 'gauge';
              return (
                <li
                  key={service.service_name}
                  className="status-service-tile"
                  style={{ backgroundColor: colors.surfaceSecondary, borderColor: colors.border }}
                >
                  <span className="status-tile-icon" style={{ color: colors.textSecondary }} aria-hidden="true">
                    <FeatureIcon name={iconName} size={20} />
                  </span>
                  <div className="min-w-0">
                    <div className="status-tile-head">
                      <h3 className="font-semibold capitalize" style={{ color: colors.textPrimary }}>
                        {service.service_name}
                      </h3>
                      <span
                        className="status-tile-state"
                        style={{ color: colors.textPrimary }}
                      >
                        <span className="status-dot" style={{ backgroundColor: dot }} aria-hidden="true" />
                        <span className="capitalize">{service.status}</span>
                      </span>
                    </div>
                    {service.message && (
                      <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                        {service.message}
                      </p>
                    )}
                    <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                      Updated{' '}
                      <time dateTime={service.updated_at}>{formatDateTime(service.updated_at)}</time>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
};

export default ServiceStatusSection;
